import { geocodeQuery } from './map-coords.js';

export { geocodeQuery };

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

let lastSearchAt = 0;

function buildMapsUrl(lat, lng, label) {
  const q = label ? encodeURIComponent(label) : `${lat},${lng}`;
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

/** Normalize a Nominatim search result into Metis place fields. */
export function normalizeNominatimPlace(hit) {
  if (!hit?.lat || !hit?.lon) return null;

  const lat = Number(hit.lat);
  const lng = Number(hit.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const name =
    hit.name ||
    hit.display_name?.split(',')[0]?.trim() ||
    hit.display_name ||
    'Luogo';

  const placeMeta = {
    source: 'nominatim',
    displayName: hit.display_name ?? name,
    type: hit.type ?? null,
    category: hit.class ?? null,
    osmType: hit.osm_type ?? null,
    osmId: hit.osm_id ?? null,
    address: hit.address ?? null,
  };

  const placeId =
    hit.osm_type && hit.osm_id != null ? `${hit.osm_type}/${hit.osm_id}` : null;

  return {
    name,
    lat,
    lng,
    placeId,
    placeMeta,
    mapsUrl: buildMapsUrl(lat, lng, hit.display_name ?? name),
  };
}

/** Search places via Nominatim (free). Rate-limited ~1 req/s. */
export async function searchPlaces(query, { country = '', limit = 5 } = {}) {
  const q = String(query ?? '').trim();
  if (q.length < 2) return [];

  const searchQ = country ? `${q}, ${country}` : q;

  const elapsed = Date.now() - lastSearchAt;
  if (elapsed < 1100) await sleep(1100 - elapsed);

  const params = new URLSearchParams({
    q: searchQ,
    format: 'json',
    addressdetails: '1',
    limit: String(Math.min(limit, 10)),
  });

  const email = import.meta.env.VITE_NOMINATIM_EMAIL;
  if (email) params.set('email', email);

  lastSearchAt = Date.now();

  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return [];
    const rows = await res.json();
    return (rows ?? []).map(normalizeNominatimPlace).filter(Boolean);
  } catch {
    return [];
  }
}

export function placeOptionLabel(place) {
  const meta = place.placeMeta ?? place.place_meta ?? {};
  const type = meta.type ? ` · ${meta.type}` : '';
  return `${place.name}${type}`;
}

export function placeOptionSubtitle(place) {
  const meta = place.placeMeta ?? place.place_meta ?? {};
  return meta.displayName ?? '';
}

export function renderPlaceMetaSummary(placeMeta) {
  if (!placeMeta || typeof placeMeta !== 'object') return '';
  const lines = [];
  if (placeMeta.displayName) lines.push(placeMeta.displayName);
  if (placeMeta.type) lines.push(String(placeMeta.type));
  return lines.filter(Boolean).join(' · ');
}
